/**
 * The one gate every change of signed-in user passes (spec 15.4, shared devices): the
 * online sign-in, "Masuk dengan akun lain" from the PIN screen and "Lupa PIN?" all run
 * through `masuk` in ./session, which asks here twice before any other session reaches
 * the sync loop. (A PIN unlock always opens the device's active user, whose data is the
 * device's: it needs no gate.)
 *
 * 1. Right after the password is accepted, before enrolment: does anyone else have unsent
 *    changes (`perubahanMilikLain`)? Then the sign-in is refused by name and count, and
 *    nothing is pushed under the newcomer's token or cleared, unless the person confirmed
 *    "Buang N perubahan itu".
 * 2. Once the newcomer's role and place are known, before `setScope`: `siapkanPerangkat`
 *    keeps the device under the same scope or clears it for a snapshot under another
 *    (the worker half, ../sync/perangkatBersama).
 *
 * An admin's change of the served person's own role or place, read from /sesi online at start,
 * at the PIN and during the day (./session `periksaPeran`), passes the same `siapkanPerangkat`.
 *
 * Only types come from the worker module: the shell chunk stays free of its code.
 */
import type { DbClient } from '../db/client'
import { hapusCacheLaporan } from '../lib/cacheLaporan'
import {
  bacaLingkupData,
  LINGKUP_DATA_KEY,
  lingkupDataDari,
  samaUntukPenggunaAktif,
} from '../sync/lingkupData'
import type { GantiPenggunaHasil, PemilikTertunda, PenggunaBaru } from '../sync/perangkatBersama'

export type { PemilikTertunda, PenggunaBaru }

export type GerbangDb = Pick<DbClient, 'requestRaw'>

/** How long Keluar waits for the sync that sends this user's unsent work. */
export const BATAS_KIRIM_KELUAR_MS = 15_000

/** Another user's unsent changes, per person; empty when the device is free to change hands. */
export async function perubahanMilikLain(
  db: GerbangDb,
  userId: string,
): Promise<PemilikTertunda[]> {
  return (await db.requestRaw('sinkron:perubahan_lain', { user_id: userId })) as PemilikTertunda[]
}

/**
 * Ready the device for `pengguna`: same scope, it carries on; another scope (or, with
 * `buang`, another person's discarded work), it is cleared and its scope recorded. Throws
 * the worker's `perubahan_lain` when someone else's work is still unsent and not discarded.
 */
export async function siapkanPerangkat(
  db: GerbangDb,
  pengguna: PenggunaBaru,
  buang: boolean,
  aktif = false,
): Promise<GantiPenggunaHasil> {
  const hasil = (await db.requestRaw('sinkron:ganti_pengguna', {
    pengguna,
    buang,
    aktif,
  })) as GantiPenggunaHasil
  // Review C-1: the report answers the device kept go with its data.
  if (hasil.dibersihkan) hapusCacheLaporan()
  return hasil
}

export type HasilPeriksaAktif = 'sesuai' | 'dibersihkan' | 'ditunda'

/**
 * The device's active user, checked at start and at the PIN (a device of an older build,
 * or data fetched for someone else, as the defect left it): the same scope stands and is
 * recorded; another is cleared for a snapshot. Offline, with another person's work still
 * unsent, or on any failure the check waits ('ditunda') for the next start or online
 * unlock: an offline unlock is never held up and never finds an empty device.
 */
export async function periksaLingkupAktif(
  db: GerbangDb,
  sesi: {
    userId: string
    peran: PenggunaBaru['peran']
    parokiId: string | null
    lingkunganId: string | null
    /** A ketua stasi's stasi, from its session row. */
    stasiId: string | null
  },
  online: boolean,
): Promise<HasilPeriksaAktif> {
  if (!online) return 'ditunda'
  try {
    const hasil = await siapkanPerangkat(
      db,
      {
        user_id: sesi.userId,
        peran: sesi.peran,
        paroki_id: sesi.parokiId,
        lingkungan_id: sesi.lingkunganId,
        stasi_id: sesi.stasiId,
      },
      false,
      true,
    )
    if (hasil.tertahan === true) return 'ditunda'
    return hasil.dibersihkan ? 'dibersihkan' : 'sesuai'
  } catch {
    return 'ditunda'
  }
}

/**
 * What `userId` still has to send: their ops in the outbox. A queued photo, or an old page
 * waiting to become one, is no change (it is read, never uploaded).
 */
export async function perubahanSendiri(db: GerbangDb, userId: string): Promise<number> {
  const hasil = (await db.requestRaw('sinkron:perubahan_sendiri', { user_id: userId })) as {
    jumlah: number
  }
  return hasil.jumlah
}

/**
 * Keluar sends first (spec 15.4): with work of this user waiting, sync until it is gone,
 * given at most `batasMs` in all; the count still unsent comes back. A sync asked for while
 * a run is in flight answers with that run, which may have pushed before the last save, so
 * a second one is asked for: the loop has queued it and it starts after the call. Any count
 * above zero is asked about before the sign-out: nobody else can sign in over it here.
 */
export async function kirimSebelumKeluar(
  db: Pick<DbClient, 'requestRaw' | 'syncNow'>,
  userId: string,
  batasMs: number = BATAS_KIRIM_KELUAR_MS,
): Promise<number> {
  let sisa = await perubahanSendiri(db, userId)
  if (sisa === 0) return 0
  let jam: ReturnType<typeof setTimeout> | undefined
  const habis = new Promise<false>((selesai) => {
    jam = setTimeout(() => selesai(false), batasMs)
  })
  try {
    for (let putaran = 0; putaran < 2 && sisa > 0; putaran++) {
      const selesai = await Promise.race([
        db.syncNow().then(
          () => true,
          () => true,
        ),
        habis,
      ])
      if (!selesai) break
      sisa = await perubahanSendiri(db, userId)
    }
  } finally {
    clearTimeout(jam)
  }
  return sisa === 0 ? 0 : perubahanSendiri(db, userId)
}

/** A tab's own session, as its store holds it. */
export interface SesiTab {
  userId: string
  peran: PenggunaBaru['peran']
  parokiId: string | null
  lingkunganId: string | null
  /** A ketua stasi's stasi (the store's session carries it). */
  stasiId?: string | null
}

/** Who the device database serves now: the active user and the recorded scope. */
export interface PenggunaPerangkat {
  aktif: string | null
  lingkup: unknown
}

/**
 * A tab that took the database back from another tab (spec 7's hand-over) resumes with the
 * session it had; meanwhile the other tab may have signed someone else in, cleared the
 * device or signed out (spec 15.4). Reload when the database no longer serves this tab's
 * person under this tab's scope; a scope not recorded yet is the start check's business.
 */
export function tabPerluDimuatUlang(tab: SesiTab, perangkat: PenggunaPerangkat): boolean {
  if (tab.userId === '') return perangkat.aktif !== null
  if (perangkat.aktif !== tab.userId) return true
  const tercatat = bacaLingkupData(perangkat.lingkup)
  if (tercatat === null) return false
  const milikTab = lingkupDataDari({
    peran: tab.peran,
    paroki_id: tab.parokiId,
    lingkungan_id: tab.lingkunganId,
    stasi_id: tab.stasiId ?? null,
  })
  return !samaUntukPenggunaAktif(tercatat, milikTab)
}

/** Read who the database serves now (the session's `sesi_aktif`, the recorded scope). */
export async function bacaPenggunaPerangkat(
  db: Pick<DbClient, 'request'>,
): Promise<PenggunaPerangkat> {
  const baca = async (kunci: string): Promise<unknown> => {
    const { rows } = await db.request('db:query', {
      sql: 'SELECT nilai FROM sync_state WHERE kunci = ?',
      params: [kunci],
      method: 'get',
    })
    const nilai = (rows as unknown[] | undefined)?.[0]
    return nilai === undefined || nilai === null
      ? undefined
      : (JSON.parse(String(nilai)) as unknown)
  }
  const aktif = await baca('sesi_aktif')
  return {
    aktif: typeof aktif === 'string' && aktif.length > 0 ? aktif : null,
    lingkup: await baca(LINGKUP_DATA_KEY),
  }
}
